import * as React from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import styles from './PasteProductsDialog.module.css';
import type { CatalogProduct } from '../ProductPicker';

const normalise = (name: string) => name.trim().toLowerCase();

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

const DEFAULT_DESCRIPTION =
  'One product per line, e.g. copied from a spreadsheet column. Names must match exactly, ignoring case. Every workspace that has a product is added.';

export interface PasteMatch {
  matched: CatalogProduct[];
  matchedNames: string[];
  unmatched: string[];
}

/** Resolves pasted product names (one per line, or tab separated) against the catalog. */
export const matchPastedNames = (text: string, catalog: CatalogProduct[]): PasteMatch => {
  const names = Array.from(
    new Set(
      text
        .split(/[\n\t]/)
        .map((n) => n.trim())
        .filter(Boolean),
    ),
  );
  const byName = new Map<string, CatalogProduct[]>();
  catalog.forEach((p) => {
    const key = normalise(p.name);
    byName.set(key, [...(byName.get(key) ?? []), p]);
  });
  const matched: CatalogProduct[] = [];
  const matchedNames: string[] = [];
  const unmatched: string[] = [];
  names.forEach((name) => {
    const rows = byName.get(normalise(name));
    if (rows) {
      matched.push(...rows);
      matchedNames.push(rows[0].name);
    } else {
      unmatched.push(name);
    }
  });
  return { matched, matchedNames, unmatched };
};

export interface PasteProductsDialogProps {
  open: boolean;
  onClose: () => void;
  catalog: CatalogProduct[];
  onAdd: (products: CatalogProduct[]) => void;
  description?: string;
  /** Names already in the set; they're reported but not counted or added again. */
  addedNames?: ReadonlySet<string>;
  /** Hands the names that weren't found to the fuzzy finder. */
  onFuzzyFind?: (names: string[]) => void;
}

/** Bulk-add products by pasting their exact names. */
const PasteProductsDialog: React.FC<PasteProductsDialogProps> = ({
  open,
  onClose,
  catalog,
  onAdd,
  description = DEFAULT_DESCRIPTION,
  addedNames,
  onFuzzyFind,
}) => {
  const [text, setText] = React.useState('');
  const [copied, setCopied] = React.useState(false);
  const result = React.useMemo(() => (text.trim() ? matchPastedNames(text, catalog) : null), [text, catalog]);

  const isNew = (name: string) => !addedNames?.has(name);
  const matchedNames = result?.matchedNames ?? [];
  const newNames = matchedNames.filter(isNew);
  const alreadyAdded = matchedNames.length - newNames.length;
  const notFound = result?.unmatched ?? [];

  const close = () => {
    setText('');
    setCopied(false);
    onClose();
  };

  const add = () => {
    onAdd((result?.matched ?? []).filter((p) => isNew(p.name)));
    close();
  };

  const copyNotFound = async () => {
    try {
      await navigator.clipboard.writeText(notFound.join('\n'));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Paste product names</DialogTitle>
      <DialogContent>
        <DialogContentText>{description}</DialogContentText>
        <TextField
          autoFocus
          multiline
          minRows={6}
          maxRows={14}
          fullWidth
          margin="normal"
          label="Product names"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setCopied(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && newNames.length > 0) {
              e.preventDefault();
              add();
            }
          }}
          helperText="Press ⌘/Ctrl + Enter to add."
        />
        {result && (
          <Box className={styles.summaryRow}>
            <Typography variant="body2" aria-live="polite" className={styles.summary}>
              {newNames.length} matched
              {alreadyAdded > 0 && `, ${alreadyAdded} already added`}
              {notFound.length > 0 && `, ${notFound.length} not found`}
            </Typography>
          </Box>
        )}
        {notFound.length > 0 && (
          <Alert
            severity="warning"
            action={
              <Box className={styles.chips}>
                <Button color="inherit" size="small" onClick={copyNotFound}>
                  {copied ? 'Copied' : 'Copy'}
                </Button>
                {onFuzzyFind && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      // Add the exact matches first so nothing pasted is lost on the way.
                      if (newNames.length > 0) onAdd((result?.matched ?? []).filter((p) => isNew(p.name)));
                      onFuzzyFind(notFound);
                      close();
                    }}
                  >
                    Fuzzy find {notFound.length}
                  </Button>
                )}
              </Box>
            }
          >
            <Typography variant="body2" gutterBottom>
              Not found in the product catalog:
            </Typography>
            <Box className={`${styles.chips} ${styles.notFound}`}>
              {notFound.map((name) => (
                <Chip key={name} size="small" label={name} />
              ))}
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button variant="contained" disabled={newNames.length === 0} onClick={add}>
          Add {plural(newNames.length, 'product')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasteProductsDialog;
